import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { usePropertyReadOnly } from '../hooks/usePropertyReadOnly';

interface ReadOnlyGuardProps {
    children: React.ReactNode;
    /**
     * 'disable' (default): render children wrapped so they're visible but
     * inert (dimmed, no pointer events) with a tooltip explaining why —
     * use for page-body action buttons, so staff can see the capability
     * exists and learn why it's off.
     * 'hide': render `fallback` (default null) instead of children — use
     * for dropdown/menu items, where a disabled row with no room for a
     * tooltip just reads as broken.
     */
    mode?: 'disable' | 'hide';
    fallback?: React.ReactNode;
    /** Overrides the default "{property} is currently inactive..." tooltip/message. */
    tooltip?: string;
}

/**
 * JSX-level primitive for the property-inactive read-only gate — the
 * `RequireAuth`-shaped counterpart to `usePropertyReadOnly`'s imperative
 * `guard()`. Nest INSIDE `RequireAuth`, not outside, so permission
 * gating and read-only gating compose predictably (a control is visible
 * only if the user is allowed to see it AND the property is active).
 */
const ReadOnlyGuard: React.FC<ReadOnlyGuardProps> = ({ children, mode = 'disable', fallback = null, tooltip }) => {
    const { isReadOnly, reason } = usePropertyReadOnly();

    if (!isReadOnly) {
        return <>{children}</>;
    }

    if (mode === 'hide') {
        return <>{fallback}</>;
    }

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip ?? reason}</Tooltip>}>
            {/* A wrapping span is required: Bootstrap/react tooltips don't fire
                hover events on a `disabled` form control, and this also lets
                the dimmed style apply to non-form elements (links, icons). */}
            <span className="d-inline-block" style={{ opacity: 0.5, pointerEvents: 'none' }}>
                {children}
            </span>
        </OverlayTrigger>
    );
};

export default ReadOnlyGuard;
